import React, { useEffect, useState } from 'react';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import { ExportedFlags } from 'cccisd-laravel-assignment';
import IconQuill from 'cccisd-icons/quill3';
import style from './style.css';
import plansQuery from './plans.graphql';
import flagsQuery from './flags.graphql';
import useShareProjectId from 'js/hooks/useShareProjectId.js';

const Fortress = window.cccisd.fortress;
const actingPawnId = Fortress.user.acting.id;
// const providerHandle = Fortress.user.acting.role.handle;
const providerHandle = 'instructor';
const actingRole = Fortress.user.acting.role.handle;

const Flags = props => {
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [planSessions, setPlanSessions] = useState([]);
    const [deploymentIds, setDeploymentIds] = useState([]);
    const [pawnIds, setPawnIds] = useState([]);
    const [projectId, loadingProjectId] = useShareProjectId();

    useEffect(() => {
        setLoading(true);
        let ignore = false;
        async function getPlanList() {
            const data = await client.query({
                query: plansQuery,
                variables: {
                    instructorPawnId: props.selectedProvider || actingPawnId,
                },
                fetchPolicy: 'network-only',
            });

            const allPlans = data.data.roles[providerHandle].createdAssignmentPlanList;
            setPlans(allPlans);

            const deployments = new Set();
            const pawns = new Set();

            allPlans.forEach(ap => {
                ap.sessionList.forEach(session => {
                    if (session.sessionDeploymentInfo) {
                        for (const value of Object.values(session.sessionDeploymentInfo)) {
                            setPlanSessions(prev => [...prev, { ...value, label: session.label, planLabel: ap.label }]);
                            deployments.add(value.deploymentId);
                            pawns.add(value.pawnId);
                        }
                    }
                });
            });
            setDeploymentIds([...deployments]);
            setPawnIds([...pawns]);
            setLoading(false);
        }

        if (!ignore) {
            // Dont run if the effect has returned (prevent memory leaks)
            if (
                !(props.deploymentIds && props.deploymentIds.length > 0) &&
                props.selectedProvider &&
                props.selectedProvider !== 'all'
            ) {
                getPlanList();
            } else if (actingRole === 'instructor') {
                getPlanList();
            } else {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }

        return () => {
            ignore = true;
        };
    }, [props.selectedProvider, props.selectedGU]);

    useEffect(() => {
        if ((props.selectedProvider === 'all' || props.selectedStudent === 'all') && props.planSessions?.length > 0) {
            setPlanSessions(props.planSessions);
        }
    }, [props.selectedProvider, props.selectedStudent, props.planSessions]);

    if (loading || loadingProjectId) {
        return <Loader loading />;
    }

    return (
        <div>
            {!props.deploymentIds && (plans.length < 1 || deploymentIds.length < 1) ? (
                <div className="alert alert-info" style={{ textAlign: 'center', marginTop: '1em' }}>
                    <h5>
                        <IconQuill spaceRight />
                        {actingRole === 'instructor'
                            ? 'You must create and begin an assessment plan to view flags.'
                            : `There are no assessment plans for the selected ${
                                  actingRole === 'siteAdmin' ? 'student' : 'provider'
                              }`}
                    </h5>
                </div>
            ) : (
                <div style={{ backgroundColor: '#fefefe', borderRadius: '5px' }} className={style.flagsWrapper}>
                    <ExportedFlags
                        deploymentId={
                            props.deploymentIds && props.deploymentIds.length > 0 ? props.deploymentIds : deploymentIds
                        }
                        pawnIds={props.pawnIds && props.pawnIds.length > 0 ? props.pawnIds : pawnIds}
                        hideSystemId
                        customQuery={flagsQuery}
                        hideResolveAction={
                            ['siteAdmin', 'guAdmin', 'orgAdmin', 'supervisor'].includes(actingRole) ? true : false
                        }
                        showByWhitelist={[]}
                        customQueryVariables={{
                            deploymentId:
                                props.deploymentIds && props.deploymentIds.length > 0
                                    ? props.deploymentIds
                                    : deploymentIds,
                            pawnIds: props.pawnIds && props.pawnIds.length > 0 ? props.pawnIds : pawnIds,
                            projectId,
                            roleHandle: [],
                        }}
                        adjustColumns={({ columns }) => {
                            const newColumns = columns
                                .toSpliced(
                                    4,
                                    0,
                                    {
                                        name: 'assignmentProgress.pawn.fields.lastName',
                                        label: 'Reporter',
                                        render: ({ row }) => {
                                            if (row['assignmentProgress.pawn.fields.contactName']) {
                                                return row['assignmentProgress.pawn.fields.contactName'];
                                            }
                                            return row['assignmentProgress.pawn.fields.firstName']
                                                ? `${row['assignmentProgress.pawn.fields.firstName']} ${row['assignmentProgress.pawn.fields.lastName']}`
                                                : '';
                                        },
                                    },
                                    {
                                        name: 'assignmentProgress.deploymentId',
                                        label: 'Assessment (Timepoint)',
                                        render: ({ row }) => {
                                            const session = planSessions.find(
                                                s =>
                                                    s.deploymentId === row['assignmentProgress.deploymentId'] &&
                                                    s.pawnId === row['assignmentProgress.pawnId']
                                            );
                                            return session ? (
                                                <div>
                                                    {session.planLabel}
                                                    <p style={{ fontSize: 'smaller' }}>({session.label})</p>
                                                </div>
                                            ) : (
                                                ''
                                            );
                                        },
                                    }
                                )
                                .filter(col => {
                                    // The AP status info appears buggy and is useless to the provider anyways
                                    const flagStatus = col.name !== 'flagStatus' && col.label === 'Status';
                                    const language = col.label === 'Language';
                                    const lastVisited = col.label === 'Last Visited';
                                    const provider = col.label === 'Provider';
                                    return !flagStatus && !language && !lastVisited && !provider;
                                });

                            return ['guAdmin', 'orgAdmin'].includes(actingRole)
                                ? newColumns.filter(
                                      c => c.name !== 'assignmentProgress.deploymentId' && c.label !== 'Reporter'
                                  )
                                : newColumns;
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default Flags;
